import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Spinner, Alert, Modal, Table, Tabs, Tab, ProgressBar, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  User, 
  UserRole, 
  StudentProgress, 
  TeacherDashboardStats
} from '../../types';
import { getUserStats, getUsers, toggleUserStatus, updateUserProfile, deleteUserAccount, createTeacher } from '../../services/userService';
import { getAdminDashboardStats, AdminDashboardStatsExtended } from '../../services/adminService';
import { db } from '../../firebase';
import { collection, getDocs, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { utils as XLSXUtils, writeFile } from 'xlsx';
import { formatLocalDate } from '../../utils/formatters';
import { FaUsers, FaChalkboardTeacher, FaGraduationCap, FaBook, FaPlayCircle, FaClock, FaChartLine, FaDownload, FaPlus, FaUserCheck, FaUserTimes, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import 'animate.css';

interface FilterOptions {
  searchQuery: string;
  isVerified?: boolean;
  sortBy: 'createdAt' | 'name';
  sortOrder: 'asc' | 'desc';
}

interface StudentInCourse {
  userId: string;
  name?: string;
  progress: number;
}

const AdminDashboard: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  
  // حالة البيانات
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any | null>(null);
  const [studentStats, setStudentStats] = useState<AdminDashboardStatsExtended | null>(null);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [showAddTeacherModal, setShowAddTeacherModal] = useState<boolean>(false);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterVerified, setFilterVerified] = useState<string>('all');

  // Helper function to format watch time
  const formatWatchTime = (seconds: number): string => {
    if (!seconds) return '0 دقيقة';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours} ساعة ${minutes > 0 ? `${minutes} دقيقة` : ''}`;
    } else {
      return `${minutes} دقيقة`;
    }
  };
  
  // Helper function to get engagement badge color
  const getEngagementBadgeColor = (score: number): string => {
    if (score >= 75) return 'success';
    if (score >= 50) return 'info';
    if (score >= 25) return 'warning';
    return 'danger';
  };

  // Check admin permissions
  useEffect(() => {
    if (!currentUser || userData?.role !== UserRole.ADMIN) {
      navigate('/admin/login');
    }
  }, [currentUser, userData, navigate]);

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // جلب إحصائيات المستخدمين
        const userStatsData = await getUserStats();
        setStats(userStatsData);
        
        // جلب إحصائيات لوحة التحكم للأدمن
        const dashboardStats = await getAdminDashboardStats();
        if (dashboardStats) {
          setStudentStats(dashboardStats);
        }
        
        // جلب قائمة المدرسين
        const teachersData = await getUsers(UserRole.TEACHER);
        setTeachers(teachersData.users);
        
        // جلب قائمة الطلاب
        const studentsData = await getUsers(UserRole.STUDENT);
        setStudents(studentsData.users);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('حدث خطأ في جلب البيانات');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filtrar usuarios según el término de búsqueda y estado de verificación
  const filteredTeachers = teachers.filter(teacher => {
    const matchesSearch = searchTerm === '' || 
      teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      teacher.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesVerification = 
      filterVerified === 'all' || 
      (filterVerified === 'verified' && teacher.isVerified) || 
      (filterVerified === 'pending' && !teacher.isVerified);
    
    return matchesSearch && matchesVerification;
  });

  const filteredStudents = students.filter(student => {
    const matchesSearch = searchTerm === '' || 
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      student.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesVerification = 
      filterVerified === 'all' || 
      (filterVerified === 'verified' && student.isVerified) || 
      (filterVerified === 'pending' && !student.isVerified);
    
    return matchesSearch && matchesVerification;
  });

  // Manejar cambio de estado de verificación
  const handleToggleStatus = async (user: User) => {
    try {
      await toggleUserStatus(user.id, !user.isVerified);
      
      // Actualizar el estado local
      if (user.role === UserRole.TEACHER) {
        setTeachers(prev => prev.map(t => 
          t.id === user.id ? { ...t, isVerified: !t.isVerified } : t
        ));
      } else {
        setStudents(prev => prev.map(s => 
          s.id === user.id ? { ...s, isVerified: !s.isVerified } : s
        ));
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError('حدث خطأ في تحديث حالة المستخدم');
    }
  };

  // Manejar eliminación de usuario
  const handleDeleteUser = async (user: User) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) {
      try {
        await deleteUserAccount(user.id);
        
        // Actualizar el estado local
        if (user.role === UserRole.TEACHER) {
          setTeachers(prev => prev.filter(t => t.id !== user.id));
        } else {
          setStudents(prev => prev.filter(s => s.id !== user.id));
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        setError('حدث خطأ في حذف المستخدم');
      }
    }
  };

  // Manejar la apertura del modal de edición de usuario
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  // Manejar la actualización del perfil de usuario
  const handleUpdateUserProfile = async (userData: Partial<User>) => {
    if (!selectedUser) return;
    
    try {
      await updateUserProfile(selectedUser.id, userData);
      
      // Actualizar el estado local
      if (selectedUser.role === UserRole.TEACHER) {
        setTeachers(prev => prev.map(t => 
          t.id === selectedUser.id ? { ...t, ...userData } : t
        ));
      } else {
        setStudents(prev => prev.map(s => 
          s.id === selectedUser.id ? { ...s, ...userData } : s
        ));
      }
      
      setShowUserModal(false);
    } catch (error) {
      console.error('Error updating user profile:', error);
      setError('حدث خطأ في تحديث بيانات المستخدم');
    }
  };

  // Manejar la creación de un nuevo profesor
  const handleCreateTeacher = async (values: { name: string; phoneNumber: string; password: string }) => {
    try {
      const newTeacher = await createTeacher(values.name, values.phoneNumber, values.password);
      setTeachers(prev => [newTeacher, ...prev]);
      setShowAddTeacherModal(false);
    } catch (error) {
      console.error('Error creating teacher:', error);
      setError('حدث خطأ في إنشاء حساب المدرس');
    }
  };

  // Exportar datos a Excel
  const handleExport = () => {
    const usersToExport = activeTab === 'teachers' ? filteredTeachers : filteredStudents;
    
    const worksheet = XLSXUtils.json_to_sheet(usersToExport.map(user => ({
      'الاسم': user.name,
      'رقم الهاتف': user.phoneNumber || '',
      'الحالة': user.isVerified ? 'مفعل' : 'غير مفعل',
      'تاريخ الإنشاء': user.createdAt ? formatLocalDate(user.createdAt) : ''
    })));
    
    const workbook = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(workbook, worksheet, activeTab === 'teachers' ? 'المعلمين' : 'الطلاب');
    
    // استخدام تاريخ اليوم بتنسيق آمن
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    writeFile(workbook, `${activeTab === 'teachers' ? 'teachers' : 'students'}_${formattedDate}.xlsx`);
  };

  // Renderizar estadísticas generales
  const renderStats = () => {
    if (!stats) return null;
    
    return (
      <Row className="mb-4">
        <Col md={3} className="mb-4">
          <Card className="h-100 dashboard-card animate__animated animate__fadeInUp">
            <Card.Body className="d-flex flex-column align-items-center text-center">
              <div className="stat-icon-container mb-3">
                <FaUsers className="stat-icon text-primary" />
              </div>
              <h2 className="stat-number">{stats.students.total}</h2>
              <Card.Title className="stat-title">إجمالي الطلاب</Card.Title>
              <div className="mt-2 d-flex gap-2">
                <Badge bg="success" className="stat-badge">
                  <FaUserCheck className="me-1" /> مفعل: {stats.students.verified}
                </Badge>
                <Badge bg="warning" className="stat-badge">
                  <FaUserTimes className="me-1" /> معلق: {stats.students.pending}
                </Badge>
              </div>
              <ProgressBar className="mt-3 w-100">
                <ProgressBar variant="success" now={(stats.students.verified / stats.students.total) * 100} key={1} />
                <ProgressBar variant="warning" now={(stats.students.pending / stats.students.total) * 100} key={2} />
              </ProgressBar>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-4">
          <Card className="h-100 dashboard-card animate__animated animate__fadeInUp" style={{ animationDelay: '0.1s' }}>
            <Card.Body className="d-flex flex-column align-items-center text-center">
              <div className="stat-icon-container mb-3">
                <FaChalkboardTeacher className="stat-icon text-success" />
              </div>
              <h2 className="stat-number">{stats.teachers.total}</h2>
              <Card.Title className="stat-title">إجمالي المدرسين</Card.Title>
              <div className="mt-2 d-flex gap-2">
                <Badge bg="success" className="stat-badge">
                  <FaUserCheck className="me-1" /> مفعل: {stats.teachers.verified}
                </Badge>
                <Badge bg="warning" className="stat-badge">
                  <FaUserTimes className="me-1" /> معلق: {stats.teachers.pending}
                </Badge>
              </div>
              <ProgressBar className="mt-3 w-100">
                <ProgressBar variant="success" now={(stats.teachers.verified / stats.teachers.total) * 100} key={1} />
                <ProgressBar variant="warning" now={(stats.teachers.pending / stats.teachers.total) * 100} key={2} />
              </ProgressBar>
            </Card.Body>
          </Card>
        </Col>
        {studentStats && (
          <>
            <Col md={3} className="mb-4">
              <Card className="h-100 dashboard-card animate__animated animate__fadeInUp" style={{ animationDelay: '0.2s' }}>
                <Card.Body className="d-flex flex-column align-items-center text-center">
                  <div className="stat-icon-container mb-3">
                    <FaChartLine className="stat-icon text-info" />
                  </div>
                  <h2 className="stat-number">{studentStats.averageProgress ? studentStats.averageProgress.toFixed(1) : 0}%</h2>
                  <Card.Title className="stat-title">متوسط تقدم الطلاب</Card.Title>
                  <ProgressBar 
                    variant={studentStats.averageProgress > 75 ? "success" : studentStats.averageProgress > 50 ? "info" : studentStats.averageProgress > 25 ? "warning" : "danger"} 
                    now={studentStats.averageProgress || 0} 
                    className="mt-3 w-100"
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 dashboard-card animate__animated animate__fadeInUp" style={{ animationDelay: '0.3s' }}>
                <Card.Body className="d-flex flex-column align-items-center text-center">
                  <div className="stat-icon-container mb-3">
                    <FaClock className="stat-icon text-warning" />
                  </div>
                  <h2 className="stat-number">{formatWatchTime(studentStats.totalWatchTime || 0)}</h2>
                  <Card.Title className="stat-title">إجمالي وقت المشاهدة</Card.Title>
                  <div className="mt-3 small text-muted">
                    متوسط المشاهدة للطالب: {formatWatchTime((studentStats.totalWatchTime || 0) / (stats.students.total || 1))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 dashboard-card animate__animated animate__fadeInUp" style={{ animationDelay: '0.4s' }}>
                <Card.Body className="d-flex flex-column align-items-center text-center">
                  <div className="stat-icon-container mb-3">
                    <FaBook className="stat-icon text-danger" />
                  </div>
                  <h2 className="stat-number">{studentStats.totalCourses || 0}</h2>
                  <Card.Title className="stat-title">إجمالي الدورات</Card.Title>
                  <div className="mt-3 small text-muted">
                    دورات نشطة: {studentStats.activeCourses || 0}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="h-100 dashboard-card animate__animated animate__fadeInUp" style={{ animationDelay: '0.5s' }}>
                <Card.Body className="d-flex flex-column align-items-center text-center">
                  <div className="stat-icon-container mb-3">
                    <FaPlayCircle className="stat-icon text-primary" />
                  </div>
                  <h2 className="stat-number">{studentStats.totalLessons || 0}</h2>
                  <Card.Title className="stat-title">إجمالي الدروس</Card.Title>
                  <div className="mt-3 small text-muted">
                    متوسط الدروس لكل دورة: {((studentStats.totalLessons || 0) / (studentStats.totalCourses || 1)).toFixed(1)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="h-100 dashboard-card animate__animated animate__fadeInUp" style={{ animationDelay: '0.6s' }}>
                <Card.Body>
                  <h5 className="card-title mb-4">
                    <FaGraduationCap className="me-2 text-primary" />
                    نشاط الطلاب (آخر 7 أيام)
                  </h5>
                  <div className="activity-chart">
                    {studentStats.weeklyActivity && studentStats.weeklyActivity.map((day, index) => (
                      <div key={index} className="activity-day">
                        <div className="activity-bar-container">
                          <div 
                            className="activity-bar" 
                            style={{ 
                              height: `${Math.max(5, (day.count / (studentStats.maxDailyActivity || 1)) * 100)}%`,
                              backgroundColor: day.count > (studentStats.maxDailyActivity || 1) * 0.7 ? '#28a745' : 
                                              day.count > (studentStats.maxDailyActivity || 1) * 0.4 ? '#17a2b8' : '#ffc107'
                            }}
                          ></div>
                        </div>
                        <div className="activity-day-label">{day.day}</div>
                        <div className="activity-count">{day.count}</div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </>
        )}
      </Row>
    );
  };

  // Renderizar el panel de control principal
  return (
    <Container fluid className="py-4">
      <style>
        {`
          .dashboard-card {
            border-radius: 15px;
            border: none;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            overflow: hidden;
          }
          
          .dashboard-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          }
          
          .stat-icon-container {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-color: rgba(var(--bs-primary-rgb), 0.1);
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 1rem;
          }
          
          .stat-icon {
            font-size: 28px;
          }
          
          .stat-number {
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 0.5rem;
            background: linear-gradient(45deg, var(--bs-primary), var(--bs-info));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          
          .stat-title {
            font-size: 1rem;
            color: #6c757d;
            margin-bottom: 1rem;
          }
          
          .stat-badge {
            font-size: 0.8rem;
            padding: 0.4rem 0.6rem;
            border-radius: 50px;
          }
          
          .activity-chart {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            height: 180px;
            margin-top: 1rem;
          }
          
          .activity-day {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
          }
          
          .activity-bar-container {
            height: 120px;
            display: flex;
            align-items: flex-end;
            width: 100%;
          }
          
          .activity-bar {
            width: 80%;
            margin: 0 auto;
            border-radius: 4px 4px 0 0;
            transition: height 1s ease;
          }
          
          .activity-day-label {
            margin-top: 5px;
            font-size: 0.8rem;
            color: #6c757d;
          }
          
          .activity-count {
            font-size: 0.75rem;
            font-weight: bold;
            color: #495057;
          }
          
          .tab-content {
            padding-top: 1.5rem;
          }
          
          .nav-tabs .nav-link {
            border-radius: 0;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
            color: #495057;
            border: none;
            border-bottom: 3px solid transparent;
          }
          
          .nav-tabs .nav-link.active {
            color: var(--bs-primary);
            background-color: transparent;
            border-bottom: 3px solid var(--bs-primary);
          }
          
          .search-container {
            position: relative;
            margin-bottom: 1.5rem;
          }
          
          .search-container .form-control {
            padding-right: 40px;
            border-radius: 50px;
            border: 1px solid #ced4da;
          }
          
          .search-icon {
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);
            color: #6c757d;
          }
          
          .custom-modal .modal-content {
            border-radius: 15px;
            border: none;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          
          .custom-modal .modal-header {
            background: linear-gradient(45deg, var(--bs-primary), var(--bs-info));
            color: white;
            border-bottom: none;
            padding: 1.5rem;
          }
          
          .custom-modal .modal-title {
            font-weight: 600;
            display: flex;
            align-items: center;
          }
          
          .custom-modal .modal-body {
            padding: 1.5rem;
          }
          
          .custom-modal .modal-footer {
            border-top: none;
            padding: 1rem 1.5rem 1.5rem;
          }
          
          .custom-input {
            border-radius: 10px;
            padding: 0.75rem 1rem;
            border: 1px solid #ced4da;
          }
          
          .custom-input:focus {
            box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
            border-color: var(--bs-primary);
          }
          
          .form-floating > .form-control:focus ~ label,
          .form-floating > .form-control:not(:placeholder-shown) ~ label {
            color: var(--bs-primary);
            opacity: 0.8;
          }
          
          .custom-switch .form-check-input:checked {
            background-color: var(--bs-primary);
            border-color: var(--bs-primary);
          }
          
          .btn-with-icon {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .action-btn {
            width: 32px;
            height: 32px;
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.3s ease;
          }
          
          .action-btn:hover {
            transform: translateY(-2px);
          }
          
          .custom-table thead th {
            background-color: rgba(var(--bs-primary-rgb), 0.05);
            color: var(--bs-primary);
            font-weight: 600;
            border-top: none;
            padding: 0.75rem 1rem;
          }
          
          .custom-table tbody td {
            padding: 0.75rem 1rem;
            vertical-align: middle;
          }
          
          .status-badge {
            padding: 0.4rem 0.8rem;
            border-radius: 50px;
            font-size: 0.8rem;
          }
          
          .inner-card {
            border-radius: 12px;
            border: none;
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03);
            transition: all 0.3s ease;
          }
          
          .inner-card:hover {
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          }
          .course-card {
            border-radius: 10px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          
          .course-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
          }
          
          .course-students-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(600px, 1fr));
            gap: 20px;
          }
          
          @media (max-width: 767px) {
            .course-students-container {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0 animate__animated animate__fadeInDown">لوحة تحكم المسؤول</h1>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-primary" 
            onClick={() => setShowAddTeacherModal(true)}
            className="animate__animated animate__fadeInRight"
          >
            <FaPlus className="me-2" /> إضافة معلم جديد
          </Button>
          <Button 
            variant="primary" 
            onClick={handleExport}
            className="animate__animated animate__fadeInRight"
            style={{ animationDelay: '0.1s' }}
          >
            <FaDownload className="me-2" /> تصدير البيانات
          </Button>
        </div>
      </div>
      
      {error && <Alert variant="danger" className="animate__animated animate__shakeX">{error}</Alert>}
      
      {loading ? (
        <div className="text-center py-5 animate__animated animate__pulse animate__infinite">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">جاري تحميل البيانات...</p>
        </div>
      ) : (
        <>
          {renderStats()}
          
          <Card className="mb-4 dashboard-card animate__animated animate__fadeInUp" style={{ animationDelay: '0.7s' }}>
            <Card.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => k && setActiveTab(k)}
                className="mb-4 custom-tabs"
              >
                <Tab 
                  eventKey="overview" 
                  title={
                    <span>
                      <FaChartLine className="me-2" />
                      نظرة عامة
                    </span>
                  }
                >
                  <div className="overview-section animate__animated animate__fadeIn">
                    <Row>
                      <Col md={6}>
                        <Card className="mb-4 inner-card">
                          <Card.Body>
                            <h5 className="card-title mb-3">
                              <FaUsers className="me-2 text-primary" />
                              أحدث المستخدمين
                            </h5>
                            <ListGroup variant="flush">
                              {[...students, ...teachers]
                                .sort(sortUsers)
                                .slice(0, 5)
                                .map((user, index) => (
                                  <ListGroup.Item key={user.id} className="d-flex justify-content-between align-items-center py-3">
                                    <div className="d-flex align-items-center">
                                      {user.photoURL || user.profilePicture ? (
                                        <img 
                                          src={user.photoURL || user.profilePicture} 
                                          alt={user.name} 
                                          className="rounded-circle me-2" 
                                          style={{ width: '40px', height: '40px', objectFit: 'cover' }} 
                                        />
                                      ) : (
                                        <div 
                                          className="rounded-circle me-2 d-flex align-items-center justify-content-center" 
                                          style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            backgroundColor: user.role === UserRole.TEACHER ? '#17a2b8' : '#007bff',
                                            color: 'white',
                                            fontSize: '16px',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {user.name.charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                      <div>
                                        <div className="fw-bold">{user.name}</div>
                                        <div className="text-muted small">{user.phoneNumber || 'لا يوجد رقم هاتف'}</div>
                                      </div>
                                    </div>
                                    <div>
                                      <Badge bg={user.role === UserRole.TEACHER ? 'info' : 'primary'} className="me-2">
                                        {user.role === UserRole.TEACHER ? 'معلم' : 'طالب'}
                                      </Badge>
                                      <Badge bg={user.isVerified ? 'success' : 'warning'}>
                                        {user.isVerified ? 'مفعل' : 'معلق'}
                                      </Badge>
                                    </div>
                                  </ListGroup.Item>
                                ))}
                            </ListGroup>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={6}>
                        <Card className="mb-4 inner-card">
                          <Card.Body>
                            <h5 className="card-title mb-3">
                              <FaChartLine className="me-2 text-success" />
                              إحصائيات المنصة
                            </h5>
                            <ListGroup variant="flush">
                              <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                                <span>معدل التفاعل اليومي</span>
                                <Badge bg="success" pill>
                                  {studentStats?.dailyEngagementRate?.toFixed(1) || 0}%
                                </Badge>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                                <span>متوسط وقت الجلسة</span>
                                <Badge bg="primary" pill>
                                  {formatWatchTime(studentStats?.averageSessionTime || 0)}
                                </Badge>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                                <span>معدل إكمال الدورات</span>
                                <Badge bg="info" pill>
                                  {studentStats?.courseCompletionRate?.toFixed(1) || 0}%
                                </Badge>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                                <span>نسبة الطلاب النشطين</span>
                                <Badge bg="warning" pill>
                                  {studentStats?.activeStudentsPercentage?.toFixed(1) || 0}%
                                </Badge>
                              </ListGroup.Item>
                              <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
                                <span>عدد الاختبارات المكتملة</span>
                                <Badge bg="danger" pill>
                                  {studentStats?.completedQuizzes || 0}
                                </Badge>
                              </ListGroup.Item>
                            </ListGroup>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </Tab>
                <Tab 
                  eventKey="teachers" 
                  title={
                    <span>
                      <FaChalkboardTeacher className="me-2" />
                      المعلمين ({teachers.length})
                    </span>
                  }
                >
                  <div className="teachers-section animate__animated animate__fadeIn">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="search-container">
                        <Form.Control
                          type="text"
                          placeholder="بحث عن معلم..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <FaSearch className="search-icon" />
                      </div>
                      <div className="d-flex align-items-center">
                        <Form.Select
                          value={filterVerified}
                          onChange={(e) => setFilterVerified(e.target.value)}
                          className="me-2"
                          style={{ width: 'auto' }}
                        >
                          <option value="all">جميع المعلمين</option>
                          <option value="verified">المفعلين</option>
                          <option value="pending">المعلقين</option>
                        </Form.Select>
                      </div>
                    </div>
                    
                    <div className="table-responsive">
                      <Table hover className="custom-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>الاسم</th>
                            <th>رقم الهاتف</th>
                            <th>الحالة</th>
                            <th>تاريخ الإنشاء</th>
                            <th>الإجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTeachers.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                لا يوجد معلمين متطابقين مع معايير البحث
                              </td>
                            </tr>
                          ) : (
                            filteredTeachers.map((teacher, index) => (
                              <tr key={teacher.id} className="animate__animated animate__fadeIn" style={{ animationDelay: `${index * 0.05}s` }}>
                                <td>{index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {teacher.photoURL || teacher.profilePicture ? (
                                      <img 
                                        src={teacher.photoURL || teacher.profilePicture} 
                                        alt={teacher.name} 
                                        className="rounded-circle me-2" 
                                        style={{ width: '32px', height: '32px', objectFit: 'cover' }} 
                                      />
                                    ) : (
                                      <div 
                                        className="rounded-circle me-2 d-flex align-items-center justify-content-center" 
                                        style={{ 
                                          width: '32px', 
                                          height: '32px', 
                                          backgroundColor: '#17a2b8',
                                          color: 'white',
                                          fontSize: '14px',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        {teacher.name.charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    {teacher.name}
                                  </div>
                                </td>
                                <td>{teacher.phoneNumber || '-'}</td>
                                <td>
                                  <Badge 
                                    bg={teacher.isVerified ? 'success' : 'warning'}
                                    className="status-badge"
                                  >
                                    {teacher.isVerified ? 'مفعل' : 'معلق'}
                                  </Badge>
                                </td>
                                <td>{teacher.createdAt ? formatLocalDate(teacher.createdAt) : '-'}</td>
                                <td>
                                  <Button 
                                    variant={teacher.isVerified ? 'outline-warning' : 'outline-success'} 
                                    size="sm" 
                                    onClick={() => handleToggleStatus(teacher)}
                                    className="me-2 action-btn"
                                  >
                                    {teacher.isVerified ? <FaUserTimes /> : <FaUserCheck />}
                                  </Button>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={() => {
                                      setSelectedUser(teacher);
                                      setShowUserModal(true);
                                    }}
                                    className="me-2 action-btn"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleDeleteUser(teacher)}
                                    className="action-btn"
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>
                <Tab 
                  eventKey="students" 
                  title={
                    <span>
                      <FaGraduationCap className="me-2" />
                      الطلاب ({students.length})
                    </span>
                  }
                >
                  <div className="students-section animate__animated animate__fadeIn">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="search-container">
                        <Form.Control
                          type="text"
                          placeholder="بحث عن طالب..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <FaSearch className="search-icon" />
                      </div>
                      <div className="d-flex align-items-center">
                        <Form.Select
                          value={filterVerified}
                          onChange={(e) => setFilterVerified(e.target.value)}
                          className="me-2"
                          style={{ width: 'auto' }}
                        >
                          <option value="all">جميع الطلاب</option>
                          <option value="verified">المفعلين</option>
                          <option value="pending">المعلقين</option>
                        </Form.Select>
                      </div>
                    </div>
                    
                    <div className="table-responsive">
                      <Table hover className="custom-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>الاسم</th>
                            <th>رقم الهاتف</th>
                            <th>الحالة</th>
                            <th>التقدم</th>
                            <th>تاريخ الإنشاء</th>
                            <th>الإجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStudents.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center py-4">
                                لا يوجد طلاب متطابقين مع معايير البحث
                              </td>
                            </tr>
                          ) : (
                            filteredStudents.map((student, index) => (
                              <tr key={student.id} className="animate__animated animate__fadeIn" style={{ animationDelay: `${index * 0.05}s` }}>
                                <td>{index + 1}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {student.photoURL || student.profilePicture ? (
                                      <img 
                                        src={student.photoURL || student.profilePicture} 
                                        alt={student.name} 
                                        className="rounded-circle me-2" 
                                        style={{ width: '32px', height: '32px', objectFit: 'cover' }} 
                                      />
                                    ) : (
                                      <div 
                                        className="rounded-circle me-2 d-flex align-items-center justify-content-center" 
                                        style={{ 
                                          width: '32px', 
                                          height: '32px', 
                                          backgroundColor: '#007bff',
                                          color: 'white',
                                          fontSize: '14px',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        {student.name.charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    {student.name}
                                  </div>
                                </td>
                                <td>{student.phoneNumber || '-'}</td>
                                <td>
                                  <Badge 
                                    bg={student.isVerified ? 'success' : 'warning'}
                                    className="status-badge"
                                  >
                                    {student.isVerified ? 'مفعل' : 'معلق'}
                                  </Badge>
                                </td>
                                <td>
                                  <ProgressBar 
                                    now={(student as any).progress || 0} 
                                    label={`${(student as any).progress || 0}%`}
                                    variant={
                                      ((student as any).progress || 0) > 75 ? 'success' : 
                                      ((student as any).progress || 0) > 50 ? 'info' : 
                                      ((student as any).progress || 0) > 25 ? 'warning' : 'danger'
                                    }
                                  />
                                </td>
                                <td>{student.createdAt ? formatLocalDate(student.createdAt) : '-'}</td>
                                <td>
                                  <Button 
                                    variant={student.isVerified ? 'outline-warning' : 'outline-success'} 
                                    size="sm" 
                                    onClick={() => handleToggleStatus(student)}
                                    className="me-2 action-btn"
                                  >
                                    {student.isVerified ? <FaUserTimes /> : <FaUserCheck />}
                                  </Button>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={() => {
                                      setSelectedUser(student);
                                      setShowUserModal(true);
                                    }}
                                    className="me-2 action-btn"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleDeleteUser(student)}
                                    className="action-btn"
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>
                <Tab 
                  eventKey="courses" 
                  title={
                    <span>
                      <FaBook className="me-2" />
                      الكورسات والطلاب
                    </span>
                  }
                >
                  <Card className="mb-4 animate__animated animate__fadeIn">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0">الكورسات والطلاب المشتركين</h4>
                      </div>
                      
                      {loading ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-3">جاري تحميل بيانات الكورسات...</p>
                        </div>
                      ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                      ) : studentStats && studentStats.studentsInCourses ? (
                        <div>
                          {Object.keys(studentStats.studentsInCourses).length > 0 ? (
                            <div className="course-students-container">
                              {Object.entries(studentStats.studentsInCourses).map(([courseId, students]) => {
                                // البحث عن اسم الكورس
                                const courseName = studentStats.recentEnrollments.find(
                                  (enrollment) => enrollment.courseId === courseId
                                )?.courseName || 'كورس غير معروف';
                                
                                return (
                                  <Card key={courseId} className="mb-4 course-card">
                                    <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                      <h5 className="mb-0">{courseName}</h5>
                                      <Badge bg="primary" pill>
                                        {students.length} طالب
                                      </Badge>
                                    </Card.Header>
                                    <Card.Body>
                                      <div className="table-responsive">
                                        <Table hover className="custom-table">
                                          <thead>
                                            <tr>
                                              <th>#</th>
                                              <th>اسم الطالب</th>
                                              <th>نسبة التقدم</th>
                                              <th>حالة التقدم</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {students.map((student, index) => (
                                              <tr key={student.userId}>
                                                <td>{index + 1}</td>
                                                <td>{student.name || 'طالب غير معروف'}</td>
                                                <td>
                                                  <ProgressBar 
                                                    now={student.progress} 
                                                    label={`${student.progress}%`}
                                                    variant={getEngagementBadgeColor(student.progress)}
                                                  />
                                                </td>
                                                <td>
                                                  <Badge 
                                                    bg={getEngagementBadgeColor(student.progress)}
                                                  >
                                                    {student.progress > 75 ? 'ممتاز' : 
                                                     student.progress > 50 ? 'جيد' : 
                                                     student.progress > 25 ? 'متوسط' : 'ضعيف'}
                                                  </Badge>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                );
                              })}
                            </div>
                          ) : (
                            <Alert variant="info">
                              لا يوجد طلاب مسجلين في أي كورس حتى الآن.
                            </Alert>
                          )}
                        </div>
                      ) : (
                        <Alert variant="warning">
                          لم يتم العثور على بيانات الكورسات والطلاب.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </>
      )}
      {/* Modal إضافة معلم جديد */}
      <Modal 
        show={showAddTeacherModal} 
        onHide={() => setShowAddTeacherModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        className="custom-modal animate__animated animate__fadeIn"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title">
            <FaChalkboardTeacher className="me-2 text-primary" />
            إضافة معلم جديد
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            name: '',
            phoneNumber: '',
            password: ''
          }}
          validationSchema={Yup.object({
            name: Yup.string().required('الاسم مطلوب'),
            phoneNumber: Yup.string().required('رقم الهاتف مطلوب'),
            password: Yup.string().min(6, 'كلمة المرور يجب أن تكون 6 أحرف على الأقل').required('كلمة المرور مطلوبة')
          })}
          onSubmit={handleCreateTeacher}
        >
          {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Modal.Body>
                <Form.Group className="mb-3 form-floating">
                  <Form.Control
                    type="text"
                    name="name"
                    id="name"
                    placeholder="الاسم"
                    value={values.name}
                    onChange={handleChange}
                    isInvalid={touched.name && !!errors.name}
                    className="custom-input"
                  />
                  <Form.Label htmlFor="name">الاسم</Form.Label>
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3 form-floating">
                  <Form.Control
                    type="text"
                    name="phoneNumber"
                    id="phoneNumber"
                    placeholder="رقم الهاتف"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    isInvalid={touched.phoneNumber && !!errors.phoneNumber}
                    className="custom-input"
                  />
                  <Form.Label htmlFor="phoneNumber">رقم الهاتف</Form.Label>
                  <Form.Control.Feedback type="invalid">
                    {errors.phoneNumber}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3 form-floating">
                  <Form.Control
                    type="password"
                    name="password"
                    id="password"
                    placeholder="كلمة المرور"
                    value={values.password}
                    onChange={handleChange}
                    isInvalid={touched.password && !!errors.password}
                    className="custom-input"
                  />
                  <Form.Label htmlFor="password">كلمة المرور</Form.Label>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowAddTeacherModal(false)}>
                  إلغاء
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting} className="btn-with-icon">
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FaPlus className="me-2" />
                  )}
                  إضافة
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
      
      {/* Modal تعديل بيانات المستخدم */}
      <Modal 
        show={showUserModal && !!selectedUser} 
        onHide={() => setShowUserModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        className="custom-modal animate__animated animate__fadeIn"
      >
        <Modal.Header closeButton>
          <Modal.Title className="modal-title">
            <FaEdit className="me-2 text-primary" />
            تعديل بيانات المستخدم
          </Modal.Title>
        </Modal.Header>
        {selectedUser && (
          <Formik
            initialValues={{
              name: selectedUser.name || '',
              phoneNumber: selectedUser.phoneNumber || '',
              isVerified: selectedUser.isVerified
            }}
            validationSchema={Yup.object({
              name: Yup.string().required('الاسم مطلوب'),
              phoneNumber: Yup.string()
            })}
            onSubmit={handleUpdateUserProfile}
          >
            {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Modal.Body>
                  <Form.Group className="mb-3 form-floating">
                    <Form.Control
                      type="text"
                      name="name"
                      id="edit-name"
                      placeholder="الاسم"
                      value={values.name}
                      onChange={handleChange}
                      isInvalid={touched.name && !!errors.name}
                      className="custom-input"
                    />
                    <Form.Label htmlFor="edit-name">الاسم</Form.Label>
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  <Form.Group className="mb-3 form-floating">
                    <Form.Control
                      type="text"
                      name="phoneNumber"
                      id="edit-phoneNumber"
                      placeholder="رقم الهاتف"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      isInvalid={touched.phoneNumber && !!errors.phoneNumber}
                      className="custom-input"
                    />
                    <Form.Label htmlFor="edit-phoneNumber">رقم الهاتف</Form.Label>
                    <Form.Control.Feedback type="invalid">
                      {errors.phoneNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  <Form.Group className="mb-3 custom-switch">
                    <Form.Check
                      type="switch"
                      id="isVerified"
                      name="isVerified"
                      label="مفعل"
                      checked={values.isVerified}
                      onChange={handleChange}
                      className="custom-switch-input"
                    />
                  </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="outline-secondary" onClick={() => setShowUserModal(false)}>
                    إلغاء
                  </Button>
                  <Button variant="primary" type="submit" disabled={isSubmitting} className="btn-with-icon">
                    {isSubmitting ? (
                      <Spinner animation="border" size="sm" className="me-2" />
                    ) : (
                      <FaEdit className="me-2" />
                    )}
                    حفظ التغييرات
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </Container>
  );
};

// Modificar la línea 679 para manejar createdAt como número o Date
const sortUsers = (a: User, b: User) => {
  const aTime = typeof a.createdAt === 'number' ? a.createdAt : 0;
  const bTime = typeof b.createdAt === 'number' ? b.createdAt : 0;
  return bTime - aTime;
};

export default AdminDashboard;